import { Link } from "wouter";
import { ChevronRight } from "lucide-react";
import LazyImage from "@/components/LazyImage";

interface Breadcrumb {
  name: string;
  href: string;
}

interface Props {
  title: string;
  subtitle?: string;
  breadcrumbs: Breadcrumb[];
  image?: string;
}

export default function PageHero({ title, subtitle, breadcrumbs, image }: Props) {
  return (
    <section className="relative pt-20 xl:pt-[8.5rem] overflow-hidden">
      {/* Background */}
      {image ? (
        <>
          <div className="absolute inset-0">
            <LazyImage
              src={image}
              alt=""
              className="w-full h-full object-cover"
              wrapperClassName="w-full h-full"
              priority
            />
            <div className="absolute inset-0 bg-gray-900/65" />
          </div>
          <div className="relative container py-16 md:py-24">
            <nav className="flex items-center gap-1.5 text-sm mb-6" aria-label="Breadcrumb">
              <Link href="/" className="text-white/60 hover:text-white transition-colors">Hem</Link>
              {breadcrumbs.map((bc, i) => (
                <span key={bc.href} className="flex items-center gap-1.5">
                  <ChevronRight className="w-3.5 h-3.5 text-white/40" />
                  {i === breadcrumbs.length - 1 ? (
                    <span className="text-white">{bc.name}</span>
                  ) : (
                    <Link href={bc.href} className="text-white/60 hover:text-white transition-colors">{bc.name}</Link>
                  )}
                </span>
              ))}
            </nav>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-4 text-lg md:text-xl text-white max-w-2xl leading-relaxed">
                {subtitle}
              </p>
            )}
            <div className="mt-8 h-1 w-24 bg-[#2563EB] rounded-full" />
          </div>
        </>
      ) : (
        <>
          <div className="absolute inset-0 bg-gray-50" />
          <div className="relative container py-12 md:py-16 border-b border-gray-200">
            <nav className="flex items-center gap-1.5 text-sm mb-4" aria-label="Breadcrumb">
              <Link href="/" className="text-gray-500 hover:text-[#2563EB] transition-colors">Hem</Link>
              {breadcrumbs.map((bc, i) => (
                <span key={bc.href} className="flex items-center gap-1.5">
                  <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
                  {i === breadcrumbs.length - 1 ? (
                    <span className="text-[#2563EB] font-medium">{bc.name}</span>
                  ) : (
                    <Link href={bc.href} className="text-gray-500 hover:text-[#2563EB] transition-colors">{bc.name}</Link>
                  )}
                </span>
              ))}
            </nav>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-3 text-lg text-gray-500 max-w-2xl leading-relaxed">
                {subtitle}
              </p>
            )}
            <div className="mt-6 h-1 w-24 bg-[#2563EB] rounded-full" />
          </div>
        </>
      )}
    </section>
  );
}
